/**
 * charts.tsx — Signature chart vocabulary for the Pearl + Coral system.
 *
 * Every chart here is custom-styled SVG (no Recharts defaults). Drop them
 * into bento tiles directly. Each component takes its own data shape; see
 * the JSDoc per export.
 */

import * as React from 'react';
import { cn } from '../lib/utils';

const CORAL = '#FF7A5C';
const CORAL_SOFT = '#FFC7B2';
const CORAL_50 = '#FFE3D8';
const INK = '#1A1A1A';
/** Light "ink" substitute used as the dark-mode equivalent of INK for SVG fills/strokes. */
const INK_DARK = '#E8E4DE';
const LIME = '#C8FF42';
const LAVENDER = '#A78BFA';

/**
 * Reactively tracks whether the app is in dark mode (`.dark` class on <html>).
 * Charts use raw hex SVG fills that can't rely on CSS `dark:` variants, so they
 * read this to swap the near-black INK for a legible light tone on dark canvas.
 */
export function useIsDark(): boolean {
  const [isDark, setIsDark] = React.useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.documentElement;
    const update = () => setIsDark(el.classList.contains('dark'));
    update();
    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

/* ────────────────────────────────────────────────────────────────────
 * 1. Dotted / dashed vertical bar chart (Crextio "Progress")
 * ──────────────────────────────────────────────────────────────────── */

type DotBarDay = {
  label: string;
  value: number;          // 0-100
  style?: 'dotted' | 'dashed' | 'solid';
  highlight?: boolean;
  tooltip?: string;
};

export function DottedBarChart({
  data,
  height = 128,
  className,
}: {
  data: DotBarDay[];
  height?: number;
  className?: string;
}) {
  return (
    <div className={cn('flex items-end justify-between gap-2 relative w-full', className)} style={{ height }}>
      {data.map((day, i) => (
        <div key={i} className="flex flex-col items-center gap-2 flex-1 h-full justify-end relative">
          {day.tooltip && (
            <div className="absolute -top-1 bg-coral rounded-full px-3 py-1 text-[11px] font-bold text-white whitespace-nowrap z-10">
              {day.tooltip}
            </div>
          )}
          <BarShape height={day.value} style={day.style ?? 'dotted'} highlight={day.highlight} />
          <span className="text-[11px] text-ink/55 dark:text-white/55 font-medium">{day.label}</span>
        </div>
      ))}
    </div>
  );
}

function BarShape({ height, style, highlight }: { height: number; style: 'dotted' | 'dashed' | 'solid'; highlight?: boolean }) {
  const pct = Math.max(2, Math.min(100, height));
  if (style === 'solid' || highlight) {
    return <div className="w-2.5 rounded-full bg-ink dark:bg-white" style={{ height: `${pct}%` }} />;
  }
  const segCount = style === 'dotted' ? Math.max(3, Math.floor(pct / 6)) : Math.max(2, Math.floor(pct / 14));
  const segH = style === 'dotted' ? 3 : 6;
  const segGap = style === 'dotted' ? 4 : 6;
  return (
    <div className="w-2 flex flex-col-reverse items-center" style={{ height: `${pct}%` }}>
      {Array.from({ length: segCount }).map((_, i) => (
        <div
          key={i}
          className="w-2 bg-ink/35 dark:bg-white/35 rounded-full"
          style={{ height: `${segH}px`, marginBottom: `${segGap}px` }}
        />
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 2. Highlighted bar chart with tooltip (Twisty "Income Tracker")
 * ──────────────────────────────────────────────────────────────────── */

type HBar = { label: string; value: number; highlight?: boolean; valueLabel?: string };

export function HighlightedBarChart({
  data,
  height = 220,
  className,
}: {
  data: HBar[];
  height?: number;
  className?: string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className={cn('flex items-end justify-between relative w-full', className)} style={{ height }}>
      {data.map((d, i) => {
        const h = (d.value / max) * 100;
        const isHi = !!d.highlight;
        return (
          <div key={i} className="flex flex-col items-center flex-1 h-full justify-end relative">
            <div className="absolute inset-y-0 w-px bg-ink/8 dark:bg-white/10" />
            {isHi && d.valueLabel && (
              <div className="absolute -top-1 bg-ink dark:bg-white dark:text-ink text-white rounded-full px-3 py-1.5 text-[11px] font-bold whitespace-nowrap z-10">
                {d.valueLabel}
              </div>
            )}
            {isHi ? (
              <div className="w-10 bg-ink/10 dark:bg-white/15 rounded-full flex flex-col-reverse items-center justify-end relative" style={{ height: `${h}%`, minHeight: 56 }}>
                <div className="w-10 h-10 rounded-full bg-ink dark:bg-white dark:text-ink text-white flex items-center justify-center text-[12px] font-bold">
                  {d.label}
                </div>
              </div>
            ) : (
              <div className="w-9 h-9 rounded-full bg-pearl-200 dark:bg-white/10 text-ink/65 dark:text-white/65 flex items-center justify-center text-[12px] font-semibold relative" style={{ marginBottom: `${Math.min(80, h)}%` }}>
                {d.label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 3. Alternating bar histogram (Twisty footer)
 * ──────────────────────────────────────────────────────────────────── */

export function AlternatingBars({
  data,
  color = CORAL,
  height = 60,
  className,
}: {
  data: number[];
  color?: string;
  height?: number;
  className?: string;
}) {
  const isDark = useIsDark();
  const ink = isDark ? INK_DARK : INK;
  return (
    <div className={cn('flex items-end gap-0.5', className)} style={{ height }}>
      {data.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-full min-w-[3px]"
          style={{
            height: `${Math.max(8, Math.min(100, v))}%`,
            backgroundColor: i % 2 === 0 ? ink : color,
          }}
        />
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 4. Sparkline (line or area)
 * ──────────────────────────────────────────────────────────────────── */

export function Sparkline({
  data,
  color = CORAL,
  filled = false,
  height = 56,
  strokeWidth = 2,
  className,
}: {
  data: number[];
  color?: string;
  filled?: boolean;
  height?: number;
  strokeWidth?: number;
  className?: string;
}) {
  const id = React.useId();
  if (data.length < 2) return <div className={className} style={{ height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((v - min) / range) * 100;
    return [x, y] as const;
  });
  const linePath = pts.reduce((acc, [x, y], i) => acc + (i === 0 ? `M${x} ${y}` : ` L${x} ${y}`), '');
  const areaPath = `${linePath} L100 100 L0 100 Z`;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={className} style={{ width: '100%', height }}>
      {filled && (
        <>
          <defs>
            <linearGradient id={`grad-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#grad-${id})`} stroke="none" />
        </>
      )}
      <path d={linePath} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 5. Concentric ring chart (Financial "Annual profits")
 * ──────────────────────────────────────────────────────────────────── */

type Ring = { label: string; size: number; fill: string; solid?: boolean };

export function ConcentricRingChart({
  rings,
  className,
}: {
  rings?: Ring[];
  className?: string;
}) {
  const defaultRings: Ring[] = [
    { label: '', size: 280, fill: '#FFE3D8' },
    { label: '', size: 220, fill: '#FFC7B2' },
    { label: '', size: 160, fill: '#FF8F66' },
    { label: '', size: 100, fill: CORAL, solid: true },
  ];
  const items = rings ?? defaultRings;
  return (
    <div className={cn('relative w-72 h-72 mx-auto', className)}>
      {items.map((r, i) => (
        <div
          key={i}
          className="absolute top-1/2 left-1/2 rounded-full flex items-start justify-center pt-3 shadow-card"
          style={{
            width: r.size,
            height: r.size,
            backgroundColor: r.fill,
            transform: 'translate(-50%, -50%)',
            zIndex: i + 1,
          }}
        >
          <span className={cn('text-[13px] font-bold', r.solid ? 'text-white' : 'text-ink')}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 6. Circular timer with tick marks (Crextio "Time tracker")
 * ──────────────────────────────────────────────────────────────────── */

export function CircularTimer({
  time = '02:35',
  progress = 0.7,
  label = 'Work Time',
  size = 200,
  className,
}: {
  time?: string;
  progress?: number;
  label?: string;
  size?: number;
  className?: string;
}) {
  const isDark = useIsDark();
  const ink = isDark ? INK_DARK : INK;
  const center = size / 2;
  const radius = size * 0.40;
  const circ = 2 * Math.PI * radius;
  const coralDash = circ * 0.85;
  const inkDash = circ * Math.max(0, Math.min(1, progress));
  return (
    <div className={cn('relative inline-flex items-center justify-center', className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {Array.from({ length: 60 }).map((_, i) => {
          const a = (i / 60) * 2 * Math.PI;
          const r1 = radius * 1.18;
          const r2 = radius * 1.10;
          return (
            <line
              key={i}
              x1={center + Math.cos(a) * r1}
              y1={center + Math.sin(a) * r1}
              x2={center + Math.cos(a) * r2}
              y2={center + Math.sin(a) * r2}
              stroke={ink}
              strokeWidth={i % 5 === 0 ? 2 : 1}
              strokeOpacity={i % 5 === 0 ? 0.55 : 0.20}
              strokeLinecap="round"
            />
          );
        })}
        <circle cx={center} cy={center} r={radius} fill="none" stroke={CORAL} strokeWidth={10} strokeLinecap="round" strokeDasharray={`${coralDash} ${circ}`} />
        <circle cx={center} cy={center} r={radius} fill="none" stroke={ink} strokeWidth={10} strokeLinecap="round" strokeDasharray={`${inkDash} ${circ}`} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <div className="text-[34px] font-bold tracking-[-0.025em] text-ink dark:text-white tabular-nums">{time}</div>
        <div className="text-[11px] text-ink/60 dark:text-white/60 mt-1 uppercase tracking-[0.16em] font-semibold">{label}</div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 7. Gauge / arc stat
 * ──────────────────────────────────────────────────────────────────── */

export function GaugeArcStat({
  value = 36,
  label = 'Growth rate',
  size = 180,
  color = CORAL,
  background = INK,
  textColor = 'white',
  className,
}: {
  value?: number;
  label?: string;
  size?: number;
  color?: string;
  background?: string;
  textColor?: string;
  className?: string;
}) {
  const center = size / 2;
  const radius = size * 0.38;
  const circ = 2 * Math.PI * radius;
  const dash = circ * (Math.max(0, Math.min(100, value)) / 100);
  return (
    <div
      className={cn('rounded-full flex items-center justify-center relative shadow-card', className)}
      style={{ width: size, height: size, backgroundColor: background }}
    >
      <svg width={size} height={size} className="-rotate-90 absolute inset-0">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth={14} />
        <circle cx={center} cy={center} r={radius} fill="none" stroke={color} strokeWidth={14} strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
      </svg>
      <div className="relative text-center" style={{ color: textColor }}>
        <div className="text-stat-lg leading-none tabular-nums">{value}%</div>
        <div className="text-[11px] mt-2 uppercase tracking-[0.16em] opacity-70 font-semibold">{label}</div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 8. Stacked horizontal progress bars
 * ──────────────────────────────────────────────────────────────────── */

export function StackedProgress({
  segments,
  className,
}: {
  segments: { label: string; color: string; weight: number }[];
  className?: string;
}) {
  return (
    <div className={cn('flex items-end gap-2 h-20', className)}>
      {segments.map((s, i) => (
        <div key={i} className="flex flex-col gap-2" style={{ flex: s.weight }}>
          <span className="text-[11px] text-ink/55 dark:text-white/55 font-semibold">{s.label}</span>
          <div className="h-12 rounded-2xl" style={{ backgroundColor: s.color }} />
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 9. Pixel dot chart
 * ──────────────────────────────────────────────────────────────────── */

export function PixelDotChart({
  rows = 4,
  cols = 12,
  filled = 24,
  accentColor = CORAL,
  baseColor = CORAL_50,
  className,
}: {
  rows?: number;
  cols?: number;
  filled?: number;
  accentColor?: string;
  baseColor?: string;
  className?: string;
}) {
  const total = rows * cols;
  return (
    <div className={cn('grid gap-1.5', className)} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: i < filled ? accentColor : baseColor }}
        />
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 10. Waveform bar chart (Financial)
 * ──────────────────────────────────────────────────────────────────── */

export function WaveformBars({
  data,
  accentColor = CORAL,
  height = 80,
  className,
}: {
  data: number[];
  accentColor?: string;
  height?: number;
  className?: string;
}) {
  const isDark = useIsDark();
  const ink = isDark ? INK_DARK : INK;
  return (
    <div className={cn('flex items-center gap-0.5', className)} style={{ height }}>
      {data.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-full min-w-[2px]"
          style={{
            height: `${Math.max(8, Math.min(100, v))}%`,
            backgroundColor: i % 7 === 0 ? ink : accentColor,
            opacity: i % 3 === 0 ? 1 : 0.72,
          }}
        />
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 11. Wave / smooth stock line
 * ──────────────────────────────────────────────────────────────────── */

function smoothPath(pts: ReadonlyArray<readonly [number, number]>): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x1, y1] = pts[i - 1];
    const [x2, y2] = pts[i];
    const cx = (x1 + x2) / 2;
    d += ` Q ${cx} ${y1} ${cx} ${(y1 + y2) / 2} T ${x2} ${y2}`;
  }
  return d;
}

export function WaveStockChart({
  data,
  color = CORAL,
  height = 120,
  filled = false,
  className,
}: {
  data: number[];
  color?: string;
  height?: number;
  filled?: boolean;
  className?: string;
}) {
  const id = React.useId();
  if (data.length < 2) return <div className={className} style={{ height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * 100,
    100 - ((v - min) / range) * 100,
  ] as const);
  const path = smoothPath(pts);
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={className} style={{ width: '100%', height }}>
      {filled && (
        <>
          <defs>
            <linearGradient id={`wave-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.30} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={`${path} L100 100 L0 100 Z`} fill={`url(#wave-${id})`} stroke="none" />
        </>
      )}
      <path d={path} fill="none" stroke={color} strokeWidth={1.4} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 12. Twin progress bars (mobile salary list)
 * ──────────────────────────────────────────────────────────────────── */

export function TwinProgressBars({
  primary,
  secondary,
  primaryColor = CORAL,
  className,
}: {
  primary: { label: string; pct: number };
  secondary?: { label: string; pct: number };
  primaryColor?: string;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2 w-full', className)}>
      <div>
        <div className="text-[11px] font-semibold text-ink/60 dark:text-white/60 mb-1">{primary.label}</div>
        <div className="h-1.5 bg-pearl-200 dark:bg-white/10 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, primary.pct)}%`, backgroundColor: primaryColor }} />
        </div>
      </div>
      {secondary && (
        <div>
          <div className="text-[11px] font-semibold text-ink/60 dark:text-white/60 mb-1">{secondary.label}</div>
          <div className="h-1.5 bg-pearl-200 dark:bg-white/10 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-ink dark:bg-white transition-all" style={{ width: `${Math.min(100, secondary.pct)}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 13. Revenue bars (the existing HomePage chart, restyled)
 * ──────────────────────────────────────────────────────────────────── */

export function RevenueBars({
  series,
  highlightLast = true,
  height = 140,
  className,
}: {
  series: number[];
  highlightLast?: boolean;
  height?: number;
  className?: string;
}) {
  const max = Math.max(1, ...series);
  return (
    <div className={cn('flex items-end gap-1.5', className)} style={{ height }}>
      {series.map((v, i) => {
        const h = (v / max) * 100;
        const isLast = i === series.length - 1;
        const cls = highlightLast && isLast ? 'bg-coral' : v > 0 ? 'bg-ink/14 dark:bg-white/20' : 'bg-ink/6 dark:bg-white/10';
        return (
          <div
            key={i}
            className={cn('flex-1 rounded-[6px] origin-bottom transition-all duration-500', cls)}
            style={{ height: `${Math.max(2, h)}%` }}
          />
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * 14. Twin line chart (smooth, two series)
 * ──────────────────────────────────────────────────────────────────── */

export function TwinLineChart({
  primary,
  secondary,
  primaryColor = CORAL,
  secondaryColor,
  height = 240,
  className,
}: {
  primary: number[];
  secondary?: number[];
  primaryColor?: string;
  secondaryColor?: string;
  height?: number;
  className?: string;
}) {
  const isDark = useIsDark();
  const resolvedSecondary = secondaryColor ?? (isDark ? INK_DARK : INK);
  const all = [...primary, ...(secondary ?? [])];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const buildPath = (arr: number[]) => {
    if (arr.length < 2) return '';
    const pts = arr.map((v, i) => [
      (i / (arr.length - 1)) * 100,
      100 - ((v - min) / range) * 100,
    ] as const);
    return smoothPath(pts);
  };
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={className} style={{ width: '100%', height }}>
      <path d={buildPath(primary)} fill="none" stroke={primaryColor} strokeWidth={1.5} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {secondary && (
        <path
          d={buildPath(secondary)}
          fill="none"
          stroke={resolvedSecondary}
          strokeWidth={1.2}
          strokeLinecap="round"
          strokeDasharray="2 1.5"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}

export const CHART_COLORS = { CORAL, CORAL_SOFT, CORAL_50, INK, LIME, LAVENDER } as const;
